/**
 * The users-admin contract: `GET|POST|PATCH /users`, `GET|PUT /users/:id/roles`,
 * `GET|PUT /users/:id/overrides`. All GLOBAL routes — no `:cc` anywhere, per D5.
 *
 * Named `users-api.ts` beside `infra-api.ts` for the reason that file states:
 * two work streams share this folder and must not race for one filename.
 *
 * Shapes are transcribed from the API source, cited per type, never guessed:
 *   · `UserView` / `PaginatedUsers`   — apps/api/src/modules/users/dto/users.dto.ts:143,155
 *   · `RoleAssignment` / `PermissionOverride` — apps/api/src/modules/rbac/rbac.types.ts:4,10
 *   · the rbac envelopes `{userId, assignments|overrides}` — rbac.controller.ts:40,74
 *
 * Unlike the inquiries directory module (`use-users.ts`), NOTHING is dropped
 * from `UserView` here: this IS the admin surface, and every field the API
 * serialises is rendered somewhere on these screens.
 *
 * Every call goes through `apiRequest` — the single-flight refresh mutex
 * (D-web-1). ESLint forbids `fetch` outside `src/lib/api.ts` precisely so a
 * module like this cannot open a second refresh path.
 */

import { apiRequest } from '../../lib/api';
import type { Paged } from '../../lib/inquiries-api';
import type { CountryCode } from '../../domain/countries';
import type { PermissionKey, RoleLabel } from '../../lib/permissions';

/* ────────────────────────────────────────────────────────────────── shapes ── */

/**
 * One `user_country_roles` row (rbac.types.ts:4).
 *
 * `role` is a `RoleLabel`, not the seeded-seven union: since the custom-roles build an
 * assignment may name a role an administrator authored, and typing it `RoleName` would
 * make a name the API legitimately returns fail to compile here.
 */
export interface RoleAssignment {
  country: CountryCode;
  role: RoleLabel;
}

/** One `user_permission_overrides` row (rbac.types.ts:10). */
export interface PermissionOverride {
  country: CountryCode;
  permission: PermissionKey;
  effect: 'grant' | 'revoke';
}

export type UserStatus = 'active' | 'disabled';

/** `UserView` (users.dto.ts:143) — `password_hash` is never serialised. */
export interface AdminUserDto {
  id: number;
  name: string;
  email: string;
  phone: string | null;
  status: UserStatus;
  is_group_admin: boolean;
  last_login_at: string | null;
  roles: RoleAssignment[];
}

/** `GET /users` query (ListUsersQueryDto, users.dto.ts:115). */
export interface ListUsersQuery {
  page?: number;
  perPage?: number;
  q?: string;
  status?: UserStatus;
  country?: CountryCode;
}

/**
 * `POST /users` (CreateUserDto, users.dto.ts:38). `roles` may be empty ONLY
 * for a `grp:true` caller creating another group admin — for everyone else the
 * server refuses with `VALIDATION_FAILED {field: 'roles'}` (d5-boundary.ts:73),
 * and the form enforces the same rule before the request.
 */
export interface CreateUserBody {
  name: string;
  email: string;
  phone?: string;
  password: string;
  is_group_admin?: boolean;
  roles: RoleAssignment[];
}

/**
 * `PATCH /users/:id` (UpdateUserDto, users.dto.ts:82) — user-RECORD fields
 * only, `grp:true` only (D5b). There is deliberately no `roles` member, so this
 * client cannot even express the legacy delete-reinsert bug.
 */
export interface UpdateUserBody {
  name?: string;
  email?: string;
  phone?: string;
  status?: UserStatus;
  password?: string;
  is_group_admin?: boolean;
}

/* ─────────────────────────────────────────────────────────────────── calls ── */

export async function listUsers(
  query: ListUsersQuery,
  signal?: AbortSignal,
): Promise<Paged<AdminUserDto>> {
  return apiRequest<Paged<AdminUserDto>>('/users', {
    query: {
      page: query.page,
      perPage: query.perPage,
      q: query.q,
      status: query.status,
      country: query.country,
    },
    signal,
  });
}

export async function fetchUser(id: string, signal?: AbortSignal): Promise<AdminUserDto> {
  return apiRequest<AdminUserDto>(`/users/${id}`, { signal });
}

export async function createUser(body: CreateUserBody): Promise<AdminUserDto> {
  return apiRequest<AdminUserDto>('/users', { method: 'POST', body });
}

export async function updateUser(id: string, body: UpdateUserBody): Promise<AdminUserDto> {
  return apiRequest<AdminUserDto>(`/users/${id}`, { method: 'PATCH', body });
}

export async function fetchUserRoles(
  id: string,
  signal?: AbortSignal,
): Promise<{ userId: number; assignments: RoleAssignment[] }> {
  return apiRequest(`/users/${id}/roles`, { signal });
}

/**
 * `PUT /users/:id/roles` — the FULL desired set, with D5 partial-replace
 * semantics on the server: a `grp:true` caller replaces every assignment; a
 * country-scoped caller replaces assignments only inside the countries where
 * they hold `users.manage`, and NAMING any other country is refused whole
 * (`COUNTRY_SCOPE_VIOLATION`). The role editor therefore sends only entries
 * for the caller's editable countries — see `UserDetailPage`.
 */
export async function putUserRoles(
  id: string,
  assignments: RoleAssignment[],
): Promise<{ userId: number; assignments: RoleAssignment[] }> {
  return apiRequest(`/users/${id}/roles`, { method: 'PUT', body: { assignments } });
}

export async function fetchUserOverrides(
  id: string,
  signal?: AbortSignal,
): Promise<{ userId: number; overrides: PermissionOverride[] }> {
  return apiRequest(`/users/${id}/overrides`, { signal });
}

/** Same D5 boundary as roles, plus the grant stops in `d5-boundary.ts:99`. */
export async function putUserOverrides(
  id: string,
  overrides: PermissionOverride[],
): Promise<{ userId: number; overrides: PermissionOverride[] }> {
  return apiRequest(`/users/${id}/overrides`, { method: 'PUT', body: { overrides } });
}

/* ──────────────────────────────────────────────────────────────── query keys ── */

/**
 * All keys nest under `['users']` — the SAME root as the inquiries colleague
 * directory (`userKeys.directory()` = `['users', 'directory']`). That is not a
 * coincidence, it is the invalidation contract: creating a user or changing
 * roles here invalidates the `['users']` prefix, so the bulk-assign picker on
 * the pipeline refetches too and a colleague created a moment ago is
 * assignable without a reload.
 */
export const adminUserKeys = {
  root: () => ['users'] as const,
  list: (query: ListUsersQuery) =>
    [
      'users',
      'admin-list',
      {
        page: query.page ?? 1,
        q: query.q ?? '',
        status: query.status ?? null,
        country: query.country ?? null,
      },
    ] as const,
  detail: (id: string) => ['users', 'admin-detail', id] as const,
  roles: (id: string) => ['users', 'admin-roles', id] as const,
  overrides: (id: string) => ['users', 'admin-overrides', id] as const,
};

/**
 * `MIN_PASSWORD_LENGTH` mirror (apps/api/src/modules/auth/auth.constants.ts:24).
 * The server is the authority (`@MinLength` on both DTOs); this exists so the
 * form can say "at least 12 characters" BEFORE the round trip. Parity is
 * asserted by `users-admin.test.tsx` reading the API source as text — the
 * `domain/countries.test.ts` pattern.
 */
export const MIN_PASSWORD_LENGTH = 12;
